import React, { useState, useMemo, useEffect, useCallback, useImperativeHandle } from 'react';
import { Edit, PlusCircle, Search, Trash2, PocketKnife, } from 'lucide-react';
import { Customer, ProductPart, SearchFieldConfig } from '../../types/mold';
import EditableModal from './EditableModal';
import PartTable from './PartTable';
import { validateAllFields, ValidationRule } from '../../utils/validation';
import { customerService } from '../../services/api';
import SearchPartModal from '../MoldDetail/SearchPartModal';
import DeleteConfirmationDialog from '../MoldDetail/DeleteConfirmationDialog';
import ActionButton from './ActionButton';
import { useProductParts } from '../../hooks/useProductParts';
import ErrorNotificationModal from './ErrorNotificationModal';
import { getErrorMessage } from '../../utils/apiError';
import { AxiosError } from 'axios';
import TableListSkeleton from './TableListSkeleton';

interface InputProductPart {
    id?: number;
    partNumber: string;
    partName: string;
    customerId: number | null;
    model: string;
    history: string;
    isActive: boolean;
    description: string;
    status: string;
    customer?: Customer;
}

export const makeValidationRules = (productParts: ProductPart[], isEditMode: boolean, currentPartId?: number): { [key: string]: ValidationRule[] } => {
    const uniquePartValidation = async (value: string) => {
        // If no data is available yet, consider it unique (validation will run again when data is loaded)
        if (!productParts || productParts.length === 0) {
            return true;
        }
        
        // If in edit mode, exclude the current part from the uniqueness check
        const partsToCheck = isEditMode && currentPartId ?
            productParts.filter(part => part.id !== currentPartId) :
            productParts;

        const isUnique = partsToCheck.every((part) => part.partNumber !== value);
        return isUnique;
    };

    return {
        partNumber: [
            {
                type: 'regex',
                regex: /^[a-zA-Z0-9-]+$/,
                message: 'รหัสชิ้นส่วนต้องประกอบด้วยตัวอักษรภาษาอังกฤษพิมพ์เล็ก ตัวเลข และอักขระพิเศษ (-) เท่านั้น',
            },
            {
                type: 'unique',
                message: 'รหัสชิ้นส่วนนี้มีอยู่แล้ว',
                uniqueValidation: uniquePartValidation,
            },
        ],
        partName: [
            {
                type: 'regex',
                regex: /^[^\u0E00-\u0E7F]+$/,
                message: 'ชื่อชิ้นส่วนต้องไม่ประกอบด้วยอักขระภาษาไทย',
            },
        ],
    };
};

export interface ProductPartManagerProps {
    // For list mode - when showing all product parts from the database
    mode?: 'list' | 'selection';
    // For selection mode - when showing selected parts (e.g., in mold details)
    productParts?: ProductPart[];
    onPartsChange?: (parts: ProductPart[]) => void;
    onAddPart?: (partId: number) => Promise<void>;
    onRemovePart?: (partId: number) => Promise<void>;
    renderActions?: (part: ProductPart) => React.ReactNode;
    showSearchPartModal?: boolean;
    showAddNewButton?: boolean;
    showActionButtons?: boolean;
    showEditButton?: boolean;
    showDeleteButton?: boolean;
    modalHeaderPrefix?: string;
    onSuccessMessage?: (message: string) => void;
    onErrorMessage?: (message: string) => void;
    moldId?: string;
    title?: string | React.ReactNode;
    searchConfig?: SearchFieldConfig;
}

export interface ProductPartManagerRef {
    openCreateModal: () => void;
}

const ProductPartManager = React.forwardRef<ProductPartManagerRef, ProductPartManagerProps>(({
    mode = 'list',
    productParts: externalProductParts,
    onPartsChange,
    onAddPart,
    onRemovePart,
    renderActions,
    showSearchPartModal = true,
    showAddNewButton = true,
    showActionButtons = true,
    showEditButton = true,
    showDeleteButton = false,
    modalHeaderPrefix = '',
    onSuccessMessage,
    onErrorMessage,
    moldId,
    title = 'ทะเบียนชิ้นส่วน',
    searchConfig
}, ref) => {
    // Use internal hook for list mode, external props for selection mode
    const {
        productParts: hookProductParts,
        loading: hookLoading,
        fetchProductParts,
        updateProductParts: hookUpdateProductParts,
        createProductPart: hookCreateProductPart
    } = useProductParts();

    // Choose data source based on mode
    const rawProductParts = mode === 'list' ? hookProductParts : (externalProductParts || []);

    // Apply search filtering
    const productParts = useMemo(() => {
        if (!searchConfig?.value || !searchConfig.value.trim()) {
            return rawProductParts;
        }

        const searchLower = searchConfig.value.toLowerCase();
        const searchField = searchConfig.field;

        return rawProductParts.filter((part) => {
            switch (searchField) {
                case 'partNumber':
                    return part.partNumber.toLowerCase().includes(searchLower);
                case 'partName':
                    return part.partName.toLowerCase().includes(searchLower);
                case 'model':
                    return part.model?.toLowerCase().includes(searchLower);
                case 'customer':
                    return (
                        part.customer.name.toLowerCase().includes(searchLower) ||
                        part.customer.code.toLowerCase().includes(searchLower)
                    );
                case 'description':
                    return part.description?.toLowerCase().includes(searchLower);
                case 'history':
                    return part.history?.toLowerCase().includes(searchLower);
                case 'all':
                default:
                    return (
                        part.partNumber.toLowerCase().includes(searchLower) ||
                        part.partName.toLowerCase().includes(searchLower) ||
                        part.model?.toLowerCase().includes(searchLower) ||
                        part.customer.name.toLowerCase().includes(searchLower) ||
                        part.customer.code.toLowerCase().includes(searchLower) ||
                        part.description?.toLowerCase().includes(searchLower) ||
                        part.history?.toLowerCase().includes(searchLower)
                    );
            }
        });
    }, [rawProductParts, searchConfig?.value, searchConfig?.field]);

    const isLoading = mode === 'list' ? hookLoading : false;

    const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPart, setSelectedPart] = useState<InputProductPart | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

    // Error notification modal state
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState('');

    // Fetch data on mount for list mode
    useEffect(() => {
        if (mode === 'list') {
            fetchProductParts();
        }
    }, [mode, fetchProductParts]);

    // Always fetch product parts for validation, regardless of mode
    // This ensures uniqueness validation always has access to all product parts
    useEffect(() => {
        if (mode === 'selection' && hookProductParts.length === 0) {
            fetchProductParts();
        }
    }, [mode, hookProductParts.length, fetchProductParts]);

    // Helper function to handle error messages
    const handleError = useCallback((message: string) => {
        if (onErrorMessage) {
            onErrorMessage(message);
        } else {
            console.log('Showing error modal for:', message);
            setErrorModalMessage(message);
            setShowErrorModal(true);
        }
    }, [onErrorMessage]);

    // Use memoization for validation rules to prevent unnecessary recalculations
    // For uniqueness validation, we should check against all product parts in the database, not just the filtered/selected ones
    const validationRules = useMemo(() => makeValidationRules(hookProductParts, isEditMode, selectedPart?.id), [hookProductParts, isEditMode, selectedPart?.id]);

    const handleRemovePart = async (partId: number) => {
        try {
            await onRemovePart?.(partId);
            if (mode === 'list') {
                // For list mode, we should delete from the database
                // This functionality might need to be implemented in the service
                if (onPartsChange) {
                    onPartsChange(productParts.filter((part) => part.id !== partId));
                }
            } else {
                // For selection mode, just remove from local list
                if (onPartsChange) {
                    onPartsChange(productParts.filter((part) => part.id !== partId));
                }
            }
            setShowDeleteConfirm(null);

            if (onSuccessMessage) {
                onSuccessMessage("ลบชิ้นส่วนสำเร็จ");
            }
        } catch (err) {
            handleError("เกิดข้อผิดพลาดในการลบชิ้นส่วน");
        }
    };

    const handleAddPart = async (partId: number, part: ProductPart) => {
        try {
            await onAddPart?.(partId);
            if (onPartsChange) {
                onPartsChange([...productParts, part]);
            }
            setIsSearchModalOpen(false);

            if (onSuccessMessage) {
                onSuccessMessage("เพิ่มชิ้นส่วนสำเร็จ");
            }
        } catch (err) {
            handleError("เกิดข้อผิดพลาดในการเพิ่มชิ้นส่วน");
        }
    };

    const openCreateModal = () => {
        setSelectedPart({
            partNumber: '',
            partName: '',
            customerId: null,
            model: '',
            history: '',
            isActive: true,
            description: '',
            status: 'active',
        });
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleCreateClick = () => openCreateModal();

    useImperativeHandle(ref, () => ({
        openCreateModal,
    }), [/* dependencies */]);

    const handleEditClick = (part: ProductPart) => {
        setSelectedPart({
            ...part,
            customerId: part.customer.id,
        });
        setIsEditMode(true);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPart(null);
        setValidationErrors({});
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (selectedPart) {
            setSelectedPart({ ...selectedPart, [name]: value } as InputProductPart);
        }
        setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedPart) return;

        const errors = await validateAllFields(selectedPart, validationRules);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        try {
            if (isEditMode && selectedPart.id !== undefined) {
                // For list mode, use the internal hook to update in database
                await hookUpdateProductParts(selectedPart.id, {
                    partNumber: selectedPart.partNumber,
                    partName: selectedPart.partName,
                    model: selectedPart.model,
                    history: selectedPart.history,
                    isActive: selectedPart.isActive,
                    description: selectedPart.description,
                    status: selectedPart.status,
                    customerId: selectedPart.customerId!,
                });

                if (onPartsChange) {
                    const updatedParts = productParts.map(part =>
                        part.id === selectedPart.id
                            ? {
                                ...part,
                                partNumber: selectedPart.partNumber,
                                partName: selectedPart.partName,
                                model: selectedPart.model,
                                history: selectedPart.history,
                                isActive: selectedPart.isActive,
                                description: selectedPart.description,
                                status: selectedPart.status,
                                customer: {
                                    ...part.customer,
                                    id: selectedPart.customerId || part.customer.id
                                }
                            }
                            : part
                    );
                    onPartsChange(updatedParts);
                }

                if (onSuccessMessage) {
                    onSuccessMessage("แก้ไขชิ้นส่วนสำเร็จ");
                }
            } else {
                // Handle creating a new part
                if (mode === 'list') {
                    // For list mode, use the internal hook to create in database
                    await hookCreateProductPart({
                        partNumber: selectedPart.partNumber,
                        partName: selectedPart.partName,
                        customerId: selectedPart.customerId!,
                        model: selectedPart.model,
                        history: selectedPart.history,
                        isActive: selectedPart.isActive,
                        description: selectedPart.description,
                        status: selectedPart.status,
                    });
                } else {
                    // Always create product parts via API to get real IDs
                    const createdPart = await hookCreateProductPart({
                        partNumber: selectedPart.partNumber,
                        partName: selectedPart.partName,
                        customerId: selectedPart.customerId!,
                        model: selectedPart.model,
                        history: selectedPart.history,
                        isActive: selectedPart.isActive,
                        description: selectedPart.description,
                        status: selectedPart.status,
                    });

                    // Add the newly created part to the selection mode list
                    if (onPartsChange && createdPart) {
                        onPartsChange([...productParts, createdPart]);
                    }

                    if (onAddPart) {
                        await onAddPart(createdPart.id);
                    }
                }

                if (onSuccessMessage) {
                    onSuccessMessage("เพิ่มชิ้นส่วนใหม่สำเร็จ");
                }
            }
            handleCloseModal();
        } catch (error) {
            console.error('Failed to save product part:', error);
            const errorMessage = getErrorMessage(error as AxiosError);
            console.error('Error message:', errorMessage);
            handleError("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + errorMessage);
        }
    };

    // Default custom render actions to match MoldDetail functionality
    const defaultRenderActions = (part: ProductPart) => (
        <div className="flex space-x-2">
            {showDeleteButton && <ActionButton
                onClick={() => setShowDeleteConfirm(part.id)}
                color="outline-danger"
                size="sm"
            >
                <Trash2 size={18} />
            </ActionButton>}
            {showEditButton && <ActionButton
                onClick={() => handleEditClick(part)}
                color="outline-primary"
                size="sm"
            >
                <Edit size={18} />
            </ActionButton>}
        </div>
    );

    // Use custom renderActions if provided, otherwise use default
    const actualRenderActions = renderActions || defaultRenderActions;

    return (
        <div>
            {isLoading && (
                <div className="text-center py-4">
                    <TableListSkeleton showSearchBar={false} />
                </div>
            )}

            {!isLoading && (
                <>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 gap-1 sm:gap-0">
                        <h1 className="text-base sm:text-2xl font-bold pt-1 sm:mb-0">
                            {title}
                        </h1>
                        {showActionButtons && (
                            <div className="flex justify-end items-center gap-1 w-full sm:w-auto">
                                {showSearchPartModal && (
                                    <ActionButton
                                        onClick={() => setIsSearchModalOpen(true)}
                                        color="outline-primary"
                                        size="md"
                                        className="rounded-full shadow-sm w-full sm:w-auto"
                                    >
                                        <Search size={20} className="mr-2" />
                                        <p className="hidden sm:block">เพิ่มชิ้นส่วนงานที่มีอยู่แล้ว</p>
                                    </ActionButton>
                                )}
                                {showAddNewButton && (
                                    <ActionButton
                                        onClick={handleCreateClick}
                                        color="primary"
                                        size="md"
                                        className="rounded-full shadow-sm w-full sm:w-auto"
                                    >
                                        <PlusCircle size={20} className="mr-2" />
                                        <p className="hidden sm:block">สร้างชิ้นส่วนงานใหม่</p>
                                    </ActionButton>
                                )}
                            </div>
                        )}
                    </div>

                    {productParts.length > 0 ? (
                        <PartTable
                            productParts={productParts}
                            renderActions={actualRenderActions}
                            hideAction={!showActionButtons}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200 text-center">
                            <div className="bg-blue-50 p-6 rounded-full mb-4 shadow-sm">
                                <PocketKnife size={40} className="text-blue-500" />
                            </div>
                            <p className="text-lg font-medium text-gray-700 mb-2">ไม่พบชิ้นส่วนที่เกี่ยวข้อง</p>
                            {isEditMode && <p className="text-sm text-gray-500 max-w-md">คุณสามารถเพิ่มชิ้นส่วนงานใหม่หรือเลือกจากชิ้นส่วนงานที่มีอยู่แล้วได้โดยใช้ปุ่มด้านบน</p>}
                        </div>
                    )}
                </>
            )}

            {showDeleteConfirm !== null && (
                <DeleteConfirmationDialog
                    setShowDeleteConfirm={setShowDeleteConfirm}
                    text={`คุณแน่ใจหรือไม่ว่าต้องการลบชิ้นส่วนนี้${moldId ? `ออกจากแม่พิมพ์รหัส ${moldId}` : ''}?`}
                    onConfirm={() => handleRemovePart(showDeleteConfirm)}
                />
            )}

            {isSearchModalOpen && showSearchPartModal && (
                <SearchPartModal
                    onClose={() => setIsSearchModalOpen(false)}
                    onAddPart={handleAddPart}
                    associatedPartIds={productParts.map((part) => part.id)}
                />
            )}

            <EditableModal<InputProductPart>
                header={`${modalHeaderPrefix}${isEditMode ? 'แก้ไขชิ้นส่วนงาน' : 'เพิ่มชิ้นส่วนงานใหม่'}`}
                isOpen={isModalOpen}
                isEditMode={isEditMode}
                item={selectedPart}
                fields={[
                    {
                        key: 'partNumber',
                        label: 'รหัสชิ้นงาน',
                        type: 'text',
                        required: true,
                        placeholder: 'พิมพ์รหัสชิ้นงาน',
                    },
                    {
                        key: 'partName',
                        label: 'ชื่อชิ้นงาน',
                        type: 'text',
                        required: true,
                        placeholder: 'พิมพ์ชื่อชิ้นงาน',
                    },
                    {
                        key: 'model',
                        label: 'รุ่น',
                        type: 'text',
                        required: false,
                        placeholder: 'พิมพ์รุ่น',
                    },
                    {
                        key: 'history',
                        label: 'History',
                        type: 'text',
                        required: false,
                        placeholder: 'พิมพ์ประวัติ',
                    },
                    {
                        key: 'customerId',
                        label: 'ลูกค้า',
                        placeholder: 'เลือกชื่อลูกค้า',
                        type: 'search_dropdown',
                        options: [],
                        fetch: customerService.getOptions,
                        required: true,
                        enforceDropdownSelection: true,
                        validationMessage: "กรุณาเลือกตัวเลือกที่ถูกต้องจากรายการ",
                        allowInvalidInput: false
                    },
                ]}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                onInputChange={handleInputChange}
                validationErrors={validationErrors}
            />

            <ErrorNotificationModal
                isOpen={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                errorMessage={errorModalMessage}
            />
        </div>
    );
});

export default ProductPartManager;